import { v2Text } from '@flows/configs/petHealthV2/steps/elements/shared'
import type { SearchableListOption } from '@flows/core/types/stepContentTypes'

export const BODY_AREAS = [
  'cardiovascular_organs',
  'digestive_organs',
  'ears',
  'endocrine_glands',
  'eyes',
  'female_genital_organs',
  'immune_system',
  'liver',
  'male_genital_organs',
  'musculoskeletal_system',
  'nervous_system',
  'oral_cavity',
  'other',
  'respiratory_organs',
  'skin',
  'systemic',
  'urinary_organs',
] as const

type BodyArea = (typeof BODY_AREAS)[number]

const ILLNESSES_BY_AREA: Record<BodyArea, readonly string[]> = {
  cardiovascular_organs: [
    'arrhythmia',
    'cardiomyopathy',
    'congestive_heart_failure',
    'heart_murmur',
    'heartworm_disease',
    'mitral_valve_disease',
    'other',
  ],
  digestive_organs: [
    'colitis',
    'foreign_body_ingestion',
    'gastric_dilatation_volvulus',
    'gastritis',
    'inflammatory_bowel_disease',
    'megaesophagus',
    'pancreatitis',
    'other',
  ],
  ears: ['aural_hematoma', 'deafness', 'ear_mites', 'otitis_externa', 'otitis_media', 'other'],
  endocrine_glands: [
    'addisons_disease',
    'cushings_disease',
    'diabetes_mellitus',
    'hyperthyroidism',
    'hypothyroidism',
    'other',
  ],
  eyes: [
    'cataracts',
    'cherry_eye',
    'conjunctivitis',
    'corneal_ulcers',
    'dry_eye',
    'ectropion',
    'entropion',
    'epiphora',
    'eyelid_tumors',
    'glaucoma',
    'iris_atrophy',
    'lens_luxation',
    'pannus',
    'progressive_retinal_atrophy',
    'retinal_detachment',
    'uveitis',
    'other',
  ],
  female_genital_organs: ['mammary_tumors', 'ovarian_cysts', 'pyometra', 'vaginitis', 'other'],
  immune_system: [
    'allergies',
    'autoimmune_hemolytic_anemia',
    'immune_mediated_thrombocytopenia',
    'lupus',
    'pemphigus',
    'other',
  ],
  liver: ['cholangitis', 'hepatic_lipidosis', 'hepatitis', 'liver_shunt', 'liver_tumors', 'other'],
  male_genital_organs: [
    'cryptorchidism',
    'prostatic_hyperplasia',
    'prostatitis',
    'testicular_tumors',
    'other',
  ],
  musculoskeletal_system: [
    'arthritis',
    'cruciate_ligament_rupture',
    'elbow_dysplasia',
    'hip_dysplasia',
    'intervertebral_disc_disease',
    'osteochondrosis',
    'patellar_luxation',
    'other',
  ],
  nervous_system: [
    'degenerative_myelopathy',
    'epilepsy',
    'meningitis',
    'vestibular_disease',
    'wobbler_syndrome',
    'other',
  ],
  oral_cavity: [
    'gingivitis',
    'malocclusion',
    'periodontal_disease',
    'stomatitis',
    'tooth_fracture',
    'tooth_resorption',
    'other',
  ],
  other: ['behavioural_disorder', 'injury_or_trauma', 'parasites', 'poisoning', 'other'],
  respiratory_organs: [
    'asthma',
    'brachycephalic_airway_syndrome',
    'bronchitis',
    'laryngeal_paralysis',
    'pneumonia',
    'tracheal_collapse',
    'other',
  ],
  skin: [
    'alopecia',
    'atopic_dermatitis',
    'flea_allergy_dermatitis',
    'hot_spots',
    'mange',
    'pyoderma',
    'skin_tumors',
    'other',
  ],
  systemic: [
    'cancer',
    'feline_immunodeficiency_virus',
    'feline_leukemia_virus',
    'leishmaniasis',
    'obesity',
    'parvovirus',
    'other',
  ],
  urinary_organs: [
    'bladder_stones',
    'chronic_kidney_disease',
    'cystitis',
    'kidney_stones',
    'urinary_incontinence',
    'urinary_tract_infection',
    'other',
  ],
}

const toConditionOptions = (
  group: 'areas' | 'illnesses',
  ids: readonly string[]
): SearchableListOption[] =>
  ids.map((value) => ({ value, label: v2Text(`conditions.${group}.${value}`) }))

export const bodyAreaOptions = () => toConditionOptions('areas', BODY_AREAS)

export const illnessOptions = (area: BodyArea) =>
  toConditionOptions('illnesses', ILLNESSES_BY_AREA[area])
